import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaChartLine, FaCalculator, FaBalanceScale, FaLaptop, FaHandshake, FaGlobeAfrica } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const ProjetsDeveloppement = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaCalculator />,
            title: "Comptabilité, Finances et Conformité",
            description: "Gestion comptable multi-devises, états financiers conformes aux exigences des bailleurs de fonds, gestion fiscale et sociale spécifique aux projets de développement.",
            color: "#0d6efd"
        },
        {
            icon: <FaBalanceScale />,
            title: "Audit et Contrôle Interne",
            description: "Mise en place de procédures de contrôle interne robustes, audit financier et comptable, certification des dépenses.",
            color: "#198754"
        },
        {
            icon: <FaChartLine />,
            title: "Conseil et Accompagnement Stratégique",
            description: "Conseil en gestion de projet, mesure d'impact, optimisation des processus et transformation digitale adaptée au contexte local.",
            color: "#dc3545"
        },
        {
            icon: <FaLaptop />,
            title: "Formations",
            description: "Formation de vos équipes locales aux outils et procédures comptables et financières, renforcement des capacités.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        comptabilite: [
            {
                title: "Mise en place et tenue de la comptabilité",
                description: "Gestion multi-devises, comptabilité de projet, adaptation aux contextes locaux africains."
            },
            {
                title: "Établissement des états financiers",
                description: "Production de rapports financiers fiables et conformes aux exigences des bailleurs de fonds (Banque Mondiale, AFD, Union Européenne, USAID, etc.)."
            },
            {
                title: "Gestion de la fiscalité",
                description: "Maîtrise des obligations fiscales des projets et programmes, optimisation fiscale, déclarations fiscales."
            },
            {
                title: "Gestion de la paie et du social",
                description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail (personnel local et expatrié), respect des réglementations locales."
            },
            {
                title: "Suivi des financements",
                description: "Suivi rigoureux des décaissements, justification des dépenses, rapprochements bancaires."
            },
            {
                title: "Gestion des procédures de passation de marchés",
                description: "Accompagnement dans le respect des procédures d'achat et d'appels d'offres des bailleurs de fonds."
            },
            {
                title: "Préparation aux audits externes",
                description: "Préparation de la documentation, des pièces justificatives et des rapports financiers nécessaires aux audits externes."
            }
        ],
        audit: [
            {
                title: "Audit interne et contrôle interne",
                description: "Mise en place de procédures de contrôle interne robustes pour prévenir les risques de fraude et de mauvaise gestion."
            },
            {
                title: "Audit préalable (due diligence)",
                description: "Évaluation des risques financiers et opérationnels avant le lancement d'un projet."
            },
            {
                title: "Audit de conformité",
                description: "Vérification de la conformité aux procédures des bailleurs de fonds et aux réglementations locales."
            },
            {
                title: "Audit externe",
                description: "Audit financier et comptable de vos projets, certification des dépenses selon les normes internationales."
            }
        ],
        conseil: [
            {
                title: "Conseil en gestion de projet",
                description: "Mise en place d'outils de suivi budgétaire et de reporting, gestion des risques financiers."
            },
            {
                title: "Mesure d'impact",
                description: "Accompagnement dans la définition et le suivi d'indicateurs d'impact social et environnemental."
            },
            {
                title: "Conseil en organisation",
                description: "Optimisation des processus administratifs et financiers, amélioration de l'efficacité opérationnelle."
            },
            {
                title: "Conseil en gestion des ressources humaines",
                description: "Optimisation des processus, gestion des compétences, recrutement adapté au contexte local."
            },
            {
                title: "Accompagnement à la transformation digitale",
                description: "Mise en place de solutions numériques adaptées au contexte local (gestion de projet, suivi financier, etc.)."
            },
            {
                title: "Conseil en matière de durabilité",
                description: "Intégration des aspects ESG (Environnementaux, Sociaux et de Gouvernance) dans vos projets."
            },
            {
                title: "Conseil en matière de conformité réglementaire",
                description: "Analyse et adaptation aux réglementations locales et internationales."
            }
        ],
        formation: [
            {
                title: "Formation aux outils comptables et financiers",
                description: "Formation de vos équipes locales aux outils et procédures comptables et financières adaptées aux projets de développement."
            },
            {
                title: "Renforcement des capacités",
                description: "Programmes de renforcement des compétences pour le personnel local, transfert de savoir-faire."
            },
            {
                title: "Formation aux procédures des bailleurs",
                description: "Formation spécifique aux exigences et procédures des principaux bailleurs de fonds internationaux."
            },
            {
                title: "Formation à la gestion de projet",
                description: "Méthodologies, outils de suivi et d'évaluation, gestion axée sur les résultats."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour les Projets de Développement</h1>
                            <p className="fs-5 mb-4">Sécurisez les fonds mobilisés par l'État, Optimisez l'Impact des projets de développement</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les défis uniques des projets de développement en Afrique
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Qu'il s'agisse d'initiatives humanitaires, de développement économique, d'infrastructures, d'éducation ou de santé, 
                                vous faites face à des défis uniques : gestion de financements internationaux, exigences de transparence et de redevabilité... 
                                CINEF-EC vous apporte une expertise pointue pour sécuriser la gestion financière et administrative des projets, 
                                et vous permettre de vous concentrer sur votre mission essentielle.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Une expertise dédiée aux projets de développement</h3>
                            <p className="mb-4">
                                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des projets et programmes de développement en Afrique.
                                Nous connaissons parfaitement les exigences des bailleurs de fonds internationaux et les spécificités des contextes locaux.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous accompagner dans la gestion rigoureuse des fonds, la conformité aux exigences des bailleurs
                                et l'optimisation de l'impact de vos projets au bénéfice des populations locales.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaHandshake className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Projets humanitaires</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaGlobeAfrica className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Projets de développement</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaLaptop className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Projets d'infrastructure</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Comptabilité, Finances et Conformité</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Audit et Contrôle Interne</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.audit.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`audit-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Conseil et Accompagnement Stratégique</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.conseil.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">4. Formations</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.formation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`formation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Vous recherchez un partenaire de confiance pour sécuriser la gestion financière de vos projets de développement ?</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée.
                                    Nous analyserons ensemble vos besoins et vous proposerons des solutions adaptées à votre contexte et à vos objectifs.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/development-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default ProjetsDeveloppement;